import type { TrialStatus, AISettings } from '$lib/types/ai';
import { TrialService } from '../TrialService';

/**
 * AI Access Control Service
 * Manages access permissions, trial status, and usage limits
 * Single Responsibility: Access control and permissions management
 */
export class AIAccessControlService {
  private trialStatus?: TrialStatus;
  private isPremium: boolean = false;
  private settings: AISettings;

  constructor(settings: AISettings) {
    this.settings = settings;
  }

  /**
   * Set trial status for usage limiting
   */
  setTrialStatus(trial?: TrialStatus): void {
    this.trialStatus = trial;
  }

  /**
   * Set premium status
   */
  setPremiumStatus(isPremium: boolean): void {
    this.isPremium = isPremium;
  }

  /**
   * Update settings
   */
  updateSettings(settings: AISettings): void {
    this.settings = settings;
  }

  /**
   * Check if analysis can be performed
   */
  canAnalyze(): boolean {
    // For development, always allow if we don't have trial status set
    if (!this.trialStatus && !this.isPremium) {
      // Set default trial status for development
      this.setTrialStatus({
        isActive: true,
        hasExpired: false,
        daysRemaining: 14,
        endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        isFreeTier: false,
        aiUsageCount: 0,
        aiDailyLimit: 100
      });
    }
    
    const accessCheck = TrialService.canUseAIFeature(this.trialStatus, this.isPremium);
    return accessCheck.allowed;
  }

  /**
   * Check comprehensive access permissions
   */
  async checkAccessPermissions(): Promise<void> {
    const isFreeTier = this.trialStatus?.isFreeTier;
    const isOpenRouter = this.settings.provider === 'openrouter';
    
    if (isFreeTier && isOpenRouter) {
      // Free users using OpenRouter - check usage
      const usageData = (globalThis as any).openRouterUsage || {};
      const dailyRemaining = usageData.dailyRemaining ?? 10;
      
      if (dailyRemaining <= 0) {
        throw new Error('Daily AI analysis limit (10 times) exceeded.');
      }
    } else {
      // Trial/premium check
      const accessCheck = TrialService.canUseAIFeature(this.trialStatus, this.isPremium);
      
      if (!accessCheck.allowed) {
        const dailyLimit = this.trialStatus?.aiDailyLimit !== undefined 
          ? this.trialStatus.aiDailyLimit 
          : 10;
        
        const errorMessages: Record<string, string> = {
          'limit_exceeded': `Daily AI analysis limit (${dailyLimit} times) exceeded.`,
          'expired': 'Trial expired. Upgrade to premium to continue.',
          'trial': 'Trial period active.',
          'premium': 'Premium user.',
          'free_tier': `Free tier usage (${accessCheck.remainingUses} remaining)`
        };
        
        throw new Error(errorMessages[accessCheck.reason || 'expired']);
      }
    }
  }

  /**
   * Get AI feature access status
   */
  getAccessStatus(): {
    allowed: boolean;
    reason?: 'trial' | 'premium' | 'free_tier' | 'limit_exceeded' | 'expired';
    remainingUses?: number;
    statusMessage: string;
  } {
    const accessCheck = TrialService.canUseAIFeature(this.trialStatus, this.isPremium);
    const statusMessage = TrialService.getStatusMessage(this.trialStatus, this.isPremium);
    
    return {
      ...accessCheck,
      statusMessage
    };
  }

  /**
   * Update usage tracking after AI operation
   */
  updateUsageTracking(): void {
    const isFreeTier = this.trialStatus?.isFreeTier;
    const isOpenRouter = this.settings.provider === 'openrouter';
    
    // Update usage for free tier (non-OpenRouter)
    if (isFreeTier && !isOpenRouter && this.trialStatus) {
      this.trialStatus = TrialService.incrementAIUsage(this.trialStatus);
      // Note: In a real Svelte app, you'd update the settings store here
    }

    // Record usage if trial is active
    if (this.trialStatus?.isActive) {
      TrialService.recordUsage();
    }
  }

  /**
   * Initialize development trial status if none exists
   */
  initializeDevTrialStatus(): void {
    // For development, set default trial status if not set
    if (!this.trialStatus && !this.isPremium) {
      this.setTrialStatus({
        isActive: true,
        hasExpired: false,
        daysRemaining: 14,
        endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        isFreeTier: false,
        aiUsageCount: 0,
        aiDailyLimit: 100
      });
    }
  }

  /**
   * Get current trial status
   */
  getTrialStatus(): TrialStatus | undefined {
    return this.trialStatus;
  }

  /**
   * Get premium status
   */
  getPremiumStatus(): boolean {
    return this.isPremium;
  }

  /**
   * Check if user is in free tier
   */
  isFreeTier(): boolean {
    return this.trialStatus?.isFreeTier === true;
  }

  /**
   * Check if trial has expired
   */
  isTrialExpired(): boolean {
    return this.trialStatus?.hasExpired === true;
  }

  /**
   * Get remaining usage count
   */
  getRemainingUsage(): number {
    if (this.isPremium) {
      return Infinity;
    }
    
    const accessCheck = TrialService.canUseAIFeature(this.trialStatus, this.isPremium);
    return accessCheck.remainingUses || 0;
  }
}